import QuestionLayout from "../QuestionLayout";
import AudioPlayButton from "../AudioPlayButton";

function normalizeChoices(config) {
  const raw = config.answerOptions ?? config.choices ?? [1, 2];

  return raw.map((choice, index) => {
    if (choice && typeof choice === "object") {
      return {
        value: choice.value,
        label: choice.label ?? choice.value
      };
    }

    return {
      value: typeof choice === "number" ? choice : index + 1,
      label: choice
    };
  });
}

export default function EarMajorTriadBinaryWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const audioValue = value._audio || {};
  const answerOptions = normalizeChoices(config);

  const layout = config.layout ?? "list"; // "row" | "list"
  const trialImageWidth = config.trialImageWidth ?? 120;
  const trialImageHeight = config.trialImageHeight ?? 80;

  function setAudioState(audioState) {
    if (mode !== "edit") return;

    onChange({
      ...value,
      _audio: audioState
    });
  }

  function setAnswer(trialId, choice) {
    if (mode !== "edit") return;

    onChange({
      ...value,
      [trialId]: choice
    });
  }

  function renderChoiceButton(trial, optionValue, label, part) {
    const selected = value[trial.id];
    const isSelected = selected === optionValue;
    const isReview = mode === "review";

    const isCorrect =
      isReview &&
      isSelected &&
      part?.correct === true;

    const isIncorrect =
      isReview &&
      isSelected &&
      part?.correct === false;

    return (
      <button
        key={String(optionValue)}
        type="button"
        onClick={() => setAnswer(trial.id, optionValue)}
        disabled={mode !== "edit"}
        style={{
          minWidth: 42,
          height: 42,
          padding: "0 12px",
          borderRadius: 21,
          fontSize: 16,
          fontWeight: 600,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: mode === "edit" ? "pointer" : "default",

          borderStyle: "solid",
          borderWidth: isReview
            ? isSelected
              ? 3
              : 2
            : 2,

          borderColor: isReview
            ? isCorrect
              ? "#2e7d32"
              : isIncorrect
              ? "#c62828"
              : "#444"
            : "#444",

          background: isReview
            ? isCorrect
              ? "#e8f5e9"
              : isIncorrect
              ? "#fdecea"
              : isSelected
              ? "#e0e0e0"
              : "transparent"
            : isSelected
            ? "#1e90ff"
            : "transparent",

          color: isReview
            ? "#000"
            : isSelected
            ? "#fff"
            : "#000"
        }}
      >
        {label}
      </button>
    );
  }

  function renderTrialCard(trial, i) {
    const part = feedback?.parts?.[trial.id];

    return (
      <div
        key={trial.id}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          minWidth: config.trialColumnWidth ?? 140
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            textAlign: "center"
          }}
        >
          {i + 1}.
        </div>

        {trial.image && (
          <img
            src={trial.image}
            alt={trial.alt ?? ""}
            style={{
              width: trial.imageWidth ?? trialImageWidth,
              height: trial.imageHeight ?? trialImageHeight,
              objectFit: "contain"
            }}
          />
        )}

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            flexWrap: "wrap"
          }}
        >
          {answerOptions.map(({ value: optionValue, label }) =>
            renderChoiceButton(trial, optionValue, label, part)
          )}
        </div>
      </div>
    );
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      {config.audio && (
        <div style={{ marginBottom: 18 }}>
          <AudioPlayButton
            src={config.audio}
            value={audioValue}
            onChange={setAudioState}
            mode={mode}
            maxListens={config.maxListens ?? 1}
          />
        </div>
      )}

      <div
        style={{
          maxWidth: config.maxWidth ?? "100%",
          color: "#111"
        }}
      >
        <div
          style={{
            display: layout === "row" ? "flex" : "block",
            gap: config.columnGap ?? 22,
            alignItems: "flex-start",
            flexWrap: config.wrapTrials === false ? "nowrap" : "wrap"
          }}
        >
          {config.trials.map((trial, i) => {
            if (layout === "row") {
              return renderTrialCard(trial, i);
            }

            const part = feedback?.parts?.[trial.id];

            return (
              <div
                key={trial.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: trial.image
                    ? "28px auto 1fr"
                    : "28px 1fr",
                  alignItems: "center",
                  columnGap: 16,
                  marginBottom: 18
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    textAlign: "right"
                  }}
                >
                  {i + 1}.
                </div>

                {trial.image && (
                  <img
                    src={trial.image}
                    alt={trial.alt ?? ""}
                    style={{
                      width: trial.imageWidth ?? trialImageWidth,
                      height: trial.imageHeight ?? trialImageHeight,
                      objectFit: "contain"
                    }}
                  />
                )}

                <div style={{ display: "flex", gap: 16 }}>
                  {answerOptions.map(({ value: optionValue, label }) =>
                    renderChoiceButton(trial, optionValue, label, part)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </QuestionLayout>
  );
}